/**
 * =============================================================================
 * IDS CRM Web - Permission capabilities (system rework R4c, W10)
 * =============================================================================
 *
 * Mirror of api/src/config/permissions.js capabilitiesForRole(). The login
 * response carries `user.capabilities` computed server-side; this fallback
 * covers sessions established before the field existed. Nav renders from ONE
 * matrix — desktop and mobile can no longer drift.
 *
 * =============================================================================
 */

const MANAGEMENT = ['manager', 'admin'];
const SALES = ['commercial', 'manager', 'admin'];
// Deal control — mirror of the API's ROLES.DEALS. Gareth 2026-08-21: the
// operations coordinator works the deal stack (opportunities, pipeline
// groups, Contracts Meeting) with commercial-level control. Their read-only
// posture on tickets/tasks/work-logs is a separate matter and unchanged.
const DEALS = ['commercial', 'manager', 'admin', 'operations_coordinator'];
const FIELD = ['engineering', 'team_leader', 'manager', 'admin'];
const ENGINEER_FAMILY = ['engineering', 'engineer', 'senior_engineer', 'desk_engineer', 'team_leader'];
// Costs/margins/rates read surfaces — mirror of the API's ROLES.MONEY.
const MONEY = ['financial', 'manager', 'admin', 'team_leader'];
// Payroll read/approve — mirror of the API's ROLES.PAYROLL_VIEW.
const PAYROLL_VIEW = ['financial', 'manager', 'admin'];
// Payroll upkeep/processing — mirror of the API's ROLES.FINANCE.
export const FINANCE_ROLES = ['financial', 'admin'];
// Book, edit and cancel scheduled engineer work — mirror of the API's
// ROLES.SCHEDULE_WORK (Gareth 2026-09-23: team leaders and up).
const SCHEDULE_WORK = ['team_leader', 'manager', 'admin'];

// Display names for role values (roles rollout RR1). 'admin' is rendered
// as Super Admin everywhere; the stored value stays 'admin'.
export const ROLE_LABELS = {
  admin: 'Super Admin',
  manager: 'Manager',
  financial: 'Financial',
  team_leader: 'Team Leader',
  operations_coordinator: 'Operations Coordinator',
  commercial: 'Commercial',
  engineering: 'Engineering',
  engineer: 'Engineering',
  senior_engineer: 'Engineering',
  desk_engineer: 'Engineering'
};

export function roleLabel(role) {
  return ROLE_LABELS[role] || (role ? role.replace(/_/g, ' ') : '');
}

// Field engineers WITHOUT team_leader — these roles get the restricted map
// view (sites/depots/work only). Mirror of the API's ENGINEER_ONLY list.
const ENGINEER_ONLY = ENGINEER_FAMILY.filter(role => role !== 'team_leader');

export function isFieldEngineer(role) {
  return ENGINEER_ONLY.includes(role);
}

// Broad read set — mirror of the API's ROLES.BROAD_READ.
const BROAD_READ = ['operations_coordinator', 'financial', 'manager', 'admin', 'team_leader'];
// Distributor read set — mirror of the API's ROLES.DISTRIBUTOR_READ.
// Engineers included (Gareth 2026-08-04): they view distributors and log
// activity; the server strips the commercial money fields for them.
const DISTRIBUTOR_READ = ['commercial', 'manager', 'admin', 'team_leader', 'operations_coordinator', 'financial', ...ENGINEER_ONLY];

export function capabilitiesForRole(role) {
  const isManagement = MANAGEMENT.includes(role);
  const isOc = role === 'operations_coordinator';
  return {
    view_dashboard: role !== 'engineering',
    use_today: ENGINEER_FAMILY.includes(role),
    use_pipeline: SALES.includes(role) || BROAD_READ.includes(role),
    use_inbox: SALES.includes(role),
    manage_distributors: isManagement,
    see_team: isManagement || role === 'team_leader' || isOc,
    use_map: isManagement || ENGINEER_FAMILY.includes(role) || isOc,
    view_reports: BROAD_READ.includes(role),
    use_on_call: FIELD.includes(role),
    open_settings: isManagement || role === 'team_leader',
    use_approvals: true,
    view_costs: MONEY.includes(role) || isOc,
    view_payroll: PAYROLL_VIEW.includes(role),
    view_distributors: DISTRIBUTOR_READ.includes(role),
    use_service_desk: role !== 'commercial',
    use_scheduling: role !== 'commercial',
    schedule_work: SCHEDULE_WORK.includes(role),
    use_ops_board: true,
    plan_ops_board: OPS_BOARD_PLAN.includes(role),
    manage_ops_board_columns: OPS_BOARD_COLUMNS.includes(role)
  };
}

// Ops Planning Board (2026-10-16) - mirrors of the API's ROLES.OPS_BOARD_PLAN
// and OPS_BOARD_COLUMNS. The API gates every route; these only decide what
// the board offers.
const OPS_BOARD_PLAN = ['operations_coordinator', 'team_leader', 'manager', 'admin', 'financial'];
const OPS_BOARD_COLUMNS = ['operations_coordinator', 'team_leader', 'manager', 'admin'];
// Who may open the opportunity behind a card (the deal pages' read set).
const OPS_BOARD_DEAL_LINK = [...DEALS, 'financial', 'team_leader'];
export const canOpenDealFromBoard = (role) => OPS_BOARD_DEAL_LINK.includes(role);

// Who presses Accept on an order - mirror of the API's ROLES.ORDER_ACCEPT.
const ORDER_ACCEPT = ['commercial', 'manager', 'admin', 'operations_coordinator', 'team_leader', 'financial'];
export const canAcceptOrders = (role) => ORDER_ACCEPT.includes(role);

// Jobs & Tickets board (2026-10-20) - mirrors of the API's WORK_BOARD_READ,
// WORK_BOARD_NOTE and WORK_BOARD_ASSIGN. Coordinators add review notes and
// allocate engineers from the board; everything else stays read only for them.
const WORK_BOARD_READ = ['team_leader', 'manager', 'admin', 'operations_coordinator'];
const WORK_BOARD_NOTE = ['team_leader', 'manager', 'admin', 'operations_coordinator'];
const WORK_BOARD_ASSIGN = ['team_leader', 'manager', 'admin', 'operations_coordinator'];
export const canSeeWorkBoard = (role) => WORK_BOARD_READ.includes(role);
export const canAddReviewNotes = (role) => WORK_BOARD_NOTE.includes(role);
export const canAssignFromBoard = (role) => WORK_BOARD_ASSIGN.includes(role);

// Deal control tiers (RR3): DEALS manage; team_leader may also move stages.
export function canManageDeals(role) {
  return DEALS.includes(role);
}

export function canMoveDealStage(role) {
  return DEALS.includes(role) || role === 'team_leader';
}

// Change a logged job's crew, and clear a job's unfinished (saved) Log Work
// with a reason - mirror of the API's ROLES.CREW_CHANGE (Gareth 2026-09-23:
// team leaders and up).
const CREW_CHANGE = ['team_leader', 'manager', 'admin'];

export function canClearLogWork(role) {
  return CREW_CHANGE.includes(role);
}

// Spare parts list and Parts used (spare parts, Push 2). Mirrors of the API's
// ROLES.SPARES_EDIT, PARTS_CORRECT, PARTS_FIT and PART_MONEY_READ. The API
// gates every route itself; these only decide what a screen offers.
export const SPARE_PARTS_ROLES = ['operations_coordinator', 'manager', 'admin']; // mirror of API ROLES.SPARES_EDIT
export const SPARE_PARTS_TAB_ROLES = [...SPARE_PARTS_ROLES, 'team_leader']; // team leaders see only "need a price"
export const canManageSpareParts = (role) => SPARE_PARTS_ROLES.includes(role);
export const canSetSparePartsCharge = (role) => ['manager', 'admin'].includes(role); // markup and an item's own charge
export const canCorrectParts = (role) => ['team_leader', 'manager', 'admin'].includes(role);
export const canFitParts = (role) => [...ENGINEER_FAMILY, 'manager', 'admin'].includes(role);
export const canSeePartMoney = (role) => [...MONEY, 'operations_coordinator'].includes(role);

// RR3/RR5: read-only roles on the operational ticket/task workspaces.
// Commercial additionally see only ticket SUMMARIES (no work logs).
export function isOperationalReadOnly(role) {
  return ['operations_coordinator', 'financial', 'commercial'].includes(role);
}

export function getCapabilities(user) {
  // Merge over the role-derived defaults: sessions established before a new
  // capability existed would otherwise read it as undefined (falsy) until the
  // next /auth/me refresh.
  return {
    ...capabilitiesForRole(user?.role || 'engineering'),
    ...(user?.capabilities || {})
  };
}
